import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '@/components/rc';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface LocationComboboxProps {
  value: string;
  onChange: (value: string) => void;
  locations: string[];
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

const LocationCombobox: React.FC<LocationComboboxProps> = ({
  value,
  onChange,
  locations,
  placeholder = 'e.g. Wine Fridge',
  className,
  autoFocus,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!value.trim()) return locations;
    const q = value.toLowerCase();
    return locations.filter((l) => l.toLowerCase().includes(q));
  }, [value, locations]);

  const shouldOpen = filtered.length > 0;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value]);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightIndex]) {
        (items[highlightIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  const handleSelect = (loc: string) => {
    onChange(loc);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!shouldOpen || !open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Popover open={open && shouldOpen} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div>
          <Input
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={className}
            autoFocus={autoFocus}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] p-0 border border-[var(--rc-border-emphasis)]',
          'bg-[var(--rc-surface-primary)] rounded-[var(--rc-radius-sm)] shadow-lg',
          'max-h-[calc(2.25rem*8)] overflow-y-auto',
          '!z-[70]'
        )}
      >
        <div ref={listRef} role="listbox">
          {filtered.slice(0, 50).map((loc, i) => (
            <button
              key={loc}
              role="option"
              aria-selected={i === highlightIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(loc);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={cn(
                'w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors',
                "font-[family-name:var(--rc-font-body)] text-[var(--rc-ink-primary)]",
                i === highlightIndex
                  ? 'bg-[var(--rc-accent-pink)]/15'
                  : 'hover:bg-[var(--rc-surface-secondary)]'
              )}
            >
              {loc}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { LocationCombobox };
export default LocationCombobox;
